import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { reportToMonitoring } from "@template/shared";
import { useEffect } from "react";

function isDashboard(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/dashboard");
}

/** Branded full-screen 404 — used as the router's `defaultNotFoundComponent`. */
export function NotFound() {
  const dashboard = isDashboard();
  return (
    <BrandScreen testId="app-not-found">
      <p className="text-6xl font-bold sm:text-7xl">404</p>
      <h1 className="mt-3 text-2xl font-semibold sm:text-3xl">Stránka nenalezena</h1>
      <p className="mt-3 max-w-md text-[color:var(--hca-ivory)]/80">
        Tento odkaz je neplatný nebo už neexistuje. Vraťte se prosím zpět.
      </p>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button asChild size="lg" data-testid="app-not-found-home">
          <Link to={dashboard ? "/dashboard" : "/"}>
            {dashboard ? "Zpět do administrace" : "Zpět na úvod"}
          </Link>
        </Button>
        {dashboard ? null : (
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            data-testid="app-not-found-terminy"
          >
            <Link to="/terminy">Prohlédnout termíny</Link>
          </Button>
        )}
      </div>
    </BrandScreen>
  );
}

/** Branded full-screen route error — used as the router's `defaultErrorComponent`. */
export function RouteErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const dashboard = isDashboard();

  useEffect(() => {
    reportToMonitoring(error, {
      type: "routeError",
      route: typeof window !== "undefined" ? window.location.pathname : "unknown",
    });
  }, [error]);

  return (
    <BrandScreen testId="app-route-error">
      <h1 className="text-2xl font-semibold sm:text-3xl">Něco se pokazilo</h1>
      <p className="mt-3 max-w-md text-[color:var(--hca-ivory)]/80">
        Při načítání stránky došlo k chybě. Zkuste to prosím znovu.
      </p>
      <div
        className="mt-4 max-w-md rounded-lg bg-white/10 p-3 text-sm text-[color:var(--hca-ivory)]/90"
        role="alert"
      >
        {error.message}
      </div>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button onClick={reset} size="lg" data-testid="app-route-error-retry">
          Zkusit znovu
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
          data-testid="app-route-error-home"
        >
          <Link to={dashboard ? "/dashboard" : "/"}>
            {dashboard ? "Do administrace" : "Na úvod"}
          </Link>
        </Button>
      </div>
    </BrandScreen>
  );
}

function BrandScreen({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-[color:var(--hca-navy)] px-4 py-20 text-center text-[color:var(--hca-ivory)]"
      data-testid={testId}
    >
      <span className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.28em]">
        HCA Moravia
      </span>
      <div className="mt-6 flex flex-col items-center">{children}</div>
    </div>
  );
}
